import { useState, useMemo, useEffect, useLayoutEffect, useRef, type HTMLAttributes, type UIEvent } from "react"
import { DayPicker, type DateRange } from "react-day-picker"
import { format } from "date-fns"
import { Dialog, DialogContent, DialogTrigger } from "../../../../components/ui/dialog"
import { ExploreWidgetConfig } from "../../config"
import { useExplorer } from "../../providers"
import { useExplorerCss } from "../../hooks/useExplorerCss"
import { useWidget } from "../../../../providers/widget"
import { CalendarUtils } from "../../../calendar/calendar-utils"
import { useFetchExplorerMeta, priceFieldForMode } from "../../data"
import FieldClear from "./pro/FieldClear"
import { useSbpLabels } from "./pro/labels"
import { useSbpCompact, SrLabel } from "./pro/compact"
import { useSbpPopover } from "./pro/useSbpPopover"
import { NARROW_QUERY } from "./pro/stickyPin"
import {
  FLEX_ALT_HEADINGS,
  FLEX_STAY_LABELS,
  enumerateAlts,
  flexTriggerLabel,
  monthOptions,
  representativeRange,
  todayIso,
  type FlexStayLength,
} from "./flexDates"

// The SBP desktop/mobile cutover lives in the shared sticky scaffold
// (#1104): one source for the JS side, explorer.css keeps the literal.
/** Months shown at each size (toolkit searchbar-pro.js:823 — the inlined
 *  mobile picker re-renders at 6, the desktop popover stays at 2). */
export const MONTHS_DESKTOP = 2
export const MONTHS_NARROW = 6
/** Forward navigation horizon (toolkit searchbar-pro.js:451-453 — "Max forward
 *  navigation: 24 months from today", enforced against the RIGHT-hand visible
 *  month). The reference's "24 months" counts the current month as the first:
 *  its clamp is `todayMonth + 23`, so the offset here is 23, not 24 — the
 *  first port shipped 24 and overshot the horizon by one month (codex r1).
 *  Backwards is already capped at the current month by `startMonth`. */
export const MAX_FORWARD_MONTHS = 23

/** The latest month the calendar may show. DELIBERATE DEVIATION from the
 *  reference formula: its 1-based `floor((m+23)/12)` year carry is wrong in
 *  exactly January (m=1 → +2 years, a 35-month horizon instead of 23). This
 *  implements the stated intent — a constant 23-month offset — in JS's
 *  zero-based month arithmetic, which normalizes the year carry correctly in
 *  every month. (Toolkit bug to be filed.) */
export function forwardHorizon(today: Date): Date {
  return new Date(today.getFullYear(), today.getMonth() + MAX_FORWARD_MONTHS, 1)
}
/** 200ms slide + margin. Mirrors the toolkit's 320ms safety-net cleanup: the
 *  class has to come off even when animationend never fires (it does not fire
 *  at all under prefers-reduced-motion, where the animation is `none`). */
const SLIDE_CLEAR_MS = 320

/** How close to the end of the narrow column counts as "at the end", in px.
 *  Roughly one month pane, so the next page is already rendered by the time the
 *  visitor's thumb gets there. */
const NARROW_GROW_PX = 320

/** Exact↔flexible height morph, toolkit `morphHeight` (searchbar-pro.js:
 *  1274-1298): `height 0.25s ease` over the measured delta. */
const FLEX_MORPH_MS = 250
/** Safety net for the morph cleanup. `transitionend` does not fire when the
 *  transition is interrupted (a second toggle mid-flight), and an abandoned
 *  inline `height` would pin the panel at a stale size — the same reason the
 *  month-slide carries `SLIDE_CLEAR_MS`. */
const FLEX_MORPH_CLEAR_MS = FLEX_MORPH_MS + 120

const DAY_MS = 86400000

function atMidnight(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
}

/**
 * The draft's check-OUT, or `undefined` while one is still pending.
 *
 * react-day-picker represents a single click as `{from: d, to: d}`, not
 * `{from: d, to: undefined}` — with no `min` prop its `addToRange` returns
 * `to: date` on the first click (9.7.0, utils/addToRange.js:22-25). The toolkit
 * keeps separate checkin/checkout slots and can never produce a zero-night stay
 * from the UI (searchbar-pro.js:691-701), so a same-day pair here means
 * check-out is still pending. Every consumer — the nights badge, the trigger
 * label, the legacy input, and APPLY — reads check-out through this instead of
 * touching `range.to`, which is what shipped a "Sep 15 – Sep 15" pill and a
 * 0-night range in the properties query while the badge in the same open dialog
 * still read "Select check-out".
 *
 * `min={1}` would make react-day-picker return `to: undefined` itself, but it
 * also strips range_start/range_end off the lone check-in (DayPicker.js:298-299
 * sets both only when `from && to`) — and those two modifiers ARE the endpoint
 * circle. Normalizing on the way out keeps the picker's painting intact.
 */
export function checkoutOf(from?: Date, to?: Date): Date | undefined {
  if (!from || !to) return undefined
  return atMidnight(to) > atMidnight(from) ? to : undefined
}

/**
 * Header badge text (toolkit searchbar-pro.js:753-765, `renderBadge`).
 * Transcribed rather than improved on: with both dates set the toolkit always
 * prints the night count, so a same-day range reads "0 nights" rather than
 * falling back to a prompt.
 *
 * `minStay` extends the transcription for #1338 parity (toolkit v3.228.0
 * badge, searchbar-pro.js): while a check-out is pending and the minimum
 * actually constrains the pick (> 1 night), the badge reads "Select
 * check-out - N-night minimum" instead of the bare prompt.
 */
export function nightsBadgeLabel(from?: Date, to?: Date, minStay: number = 1): string {
  if (!from) return 'Select check-in'
  if (!to) return minStay > 1 ? `Select check-out - ${minStay}-night minimum` : 'Select check-out'
  const diff = Math.round((atMidnight(to) - atMidnight(from)) / DAY_MS)
  const n = diff > 0 ? diff : 0
  return `${n} ${n === 1 ? 'night' : 'nights'}`
}

// --- Date-window gates (#1338 min-stay marking, #1339 advance-notice /
// booking-window blocking) --------------------------------------------------
// Pure predicates over a caller-supplied CalendarUtils instance (imported,
// not forked, per the handoff — see widgets/calendar/calendar-utils.ts). The
// explorer/SBP context never fetches per-date availability (it searches
// across many properties before any one is selected), so only the THREE
// CalendarUtils methods that work without a `dailyPrice` map are safe here:
// `isOutsideWindow`, `isNoticeBlocked` and `getMinstay` all fall through to
// their static/config-derived answer when `dateOptions(day)` is undefined.
// `isUnavailable`/`canCheckin`/`canCheckout`/`getAvailableCheckoutDays` all
// require real per-date data and would misreport EVERY day as unavailable
// here — deliberately never called.

/**
 * Whether `day` falls after the booking-window ceiling (today + the
 * feed's maxBookingDays). Toolkit parity (#1339, c669a9b): the public
 * max-calendar-days boundary.
 *
 * `CalendarUtils.isOutsideWindow` also flags days before ITS OWN internal
 * `minDate`, so this is guarded to the forward half only — past days are
 * already the DayPicker's own `{ before: today }` disabled rule, computed
 * from the component's own `today`, not the calculator's.
 */
export function isBookingWindowBlocked(calendarUtils: CalendarUtils, day: Date, today: Date): boolean {
  if (atMidnight(day) < atMidnight(today)) return false
  return calendarUtils.isOutsideWindow(format(day, 'yyyy-MM-dd'))
}

/** Toolkit parity (#1339): inside the advance-notice window. Already
 *  false for past days and whenever no window is configured — see
 *  `CalendarUtils.isNoticeBlocked`. */
export function isAdvanceNoticeBlocked(calendarUtils: CalendarUtils, day: Date): boolean {
  return calendarUtils.isNoticeBlocked(format(day, 'yyyy-MM-dd'))
}

/**
 * Effective minimum stay for a check-in date (#1071, central#3208 parity):
 * the LARGER of the per-widget static `searchbar.dateWindow.minStay` (via
 * calendarUtils, which never carries per-date data in the explorer/SBP
 * context — see the file-top note on `dateOptions`/`dailyPrice`) and the
 * fleet's real per-date aggregate from `feed/explorer-meta`'s
 * `minStayByDate` (`MIN(minimum_stay)` across the feed's available member
 * properties arriving that date).
 *
 * The aggregate is a FLOOR, never a discount: it can only tighten a date
 * where real inventory needs more nights than the admin asked for. An
 * admin who set 5 nights keeps 5 even where some property would take 2 —
 * silently undercutting that setting is a behaviour change nobody opted
 * into (#1071 review). The aggregate is clamped at 100 nights, matching
 * the toolkit's own hook and `CalendarUtils._calculateMinstay`.
 *
 * A missing/zero/non-positive fleet value means "no constraint that date"
 * in the Central contract (the date is simply omitted from the map, never
 * present as 0), so it falls through to the static answer rather than
 * reading as a 0-night minimum.
 */
export function effectiveMinStay(
  calendarUtils: CalendarUtils,
  checkin: Date,
  minStayByDate?: Record<string, number>,
): number {
  const iso = format(checkin, 'yyyy-MM-dd')
  const staticMinStay = calendarUtils.getMinstay(iso)
  const fleet = minStayByDate?.[iso]
  if (typeof fleet === 'number' && Number.isFinite(fleet) && fleet > 0) {
    return Math.max(Math.min(fleet, 100), staticMinStay)
  }
  return staticMinStay
}

/**
 * Whether `day` is a night strictly inside the picked check-in's
 * minimum-stay window (toolkit parity #1338, 84eec3f `inWindow`): after
 * check-in, before the first valid check-out (check-in + the effective
 * minimum). Only meaningful while a check-out is still pending — the caller
 * gates on that (a completed range has already resolved its nights).
 *
 * `minStayByDate` is optional (#1071) so every pre-existing call site keeps
 * compiling and behaving identically with none supplied — it is read through
 * `effectiveMinStay`, never `calendarUtils.getMinstay` directly.
 */
export function isInMinStayWindow(
  calendarUtils: CalendarUtils,
  day: Date,
  checkin: Date,
  minStayByDate?: Record<string, number>,
): boolean {
  const minStay = effectiveMinStay(calendarUtils, checkin, minStayByDate)
  // Whole calendar days after check-in, rounded like nightsBadgeLabel. A
  // spring-forward night is 23h, so `midnight + minStay * DAY_MS` lands at
  // 01:00 and kept the first valid check-out day inside the window.
  const nights = Math.round((atMidnight(day) - atMidnight(checkin)) / DAY_MS)
  return nights > 0 && nights < minStay
}

/**
 * Separator between the two dates in the trigger label: SPACE + U+2013 EN DASH
 * + SPACE, byte-identical to the toolkit (searchbar-pro.js:697 hexdumps to
 * `27 20 e2 80 93 20 27`). NOT a hyphen (0x2D), NOT an em dash (U+2014), and
 * NOT the right arrow U+2192 this port shipped with (Eli QA 2026-08-05 item 3).
 */
export const DATE_RANGE_SEPARATOR = ' – '

/** Toolkit DT.addCheckout (searchbar-pro.js:63) — the hint that occupies the
 *  right-hand half of the label while only check-in is chosen. Mirror of the
 *  `searchbar.labels.addCheckout` schema default (config.ts); the render path
 *  reads the option, this export is whenTriggerLabel's default arg + tests. */
export const ADD_CHECKOUT_LABEL = 'Add checkout'

/**
 * Empty-state placeholder — "Add dates", module parity (#935 r6 item 1: the
 * old widget wording "Any time" is retired, and the string is now an admin
 * setting). Mirror of the `searchbar.labels.whenIdle` schema default: the
 * render paths read the option via useSbpLabels (ProFilterBar's mobile
 * trigger summary reads the same option, so the desktop pill and the sheet
 * can no longer disagree about the same empty field); this export exists for
 * tests, which pin the two defaults equal.
 */
export const DATES_PLACEHOLDER = 'Add dates'

/** The `dateFormat` schema default (config.ts). A stored value equal to it is
 *  read as "never customised" — see `dateFormatIsCustomised` below. */
export const DEFAULT_DATE_FORMAT = 'MMM d, yyyy'

/**
 * Toolkit `formatDateDisplay` (searchbar-pro.js:303-313): locale-aware,
 * abbreviated month, day number, NO YEAR and no format string.
 *
 * The reference's own Intl-throw fallback reaches for DT.monthNames, which
 * defaults to the FULL English names (searchbar-pro.js:76-79) and so emits
 * "July 15" where the happy path emits "Jul 15". That is a toolkit bug and is
 * deliberately not ported; date-fns 'MMM d' gives the abbreviated form the
 * reference intends.
 */
export function formatShortDate(date: Date): string {
  try {
    return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(date)
  } catch {
    return format(date, 'MMM d')
  }
}

/**
 * The trigger label's three states, transcribed from `syncWhenTriggerLabel`
 * (searchbar-pro.js:685-716):
 *   neither endpoint  → '' (caller substitutes the placeholder + its class)
 *   check-in only     → "Jul 15 – Add checkout"
 *   both              → "Jul 15 – Jul 20"
 *
 * A check-out with no check-in cannot be produced by the UI but URL hydration
 * can; the reference falls through to its placeholder branch, so this returns
 * '' rather than rendering a lone end date.
 */
export function whenTriggerLabel(
  from: Date | undefined,
  to: Date | undefined,
  fmt: (d: Date) => string = formatShortDate,
  addCheckoutLabel: string = ADD_CHECKOUT_LABEL,
): string {
  if (!from) return ''
  if (!to) return `${fmt(from)}${DATE_RANGE_SEPARATOR}${addCheckoutLabel}`
  return `${fmt(from)}${DATE_RANGE_SEPARATOR}${fmt(to)}`
}

/** Tracks the SBP breakpoint. Starts false so SSR and the hydration pass agree
 *  (a `matchMedia` read during render would differ between them and throw the
 *  whole tree away). */
export function useNarrowViewport(): boolean {
  const [narrow, setNarrow] = useState(false)
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const mq = window.matchMedia(NARROW_QUERY)
    const apply = () => setNarrow(mq.matches)
    apply()
    mq.addEventListener?.('change', apply)
    return () => mq.removeEventListener?.('change', apply)
  }, [])
  return narrow
}

// `pro` renders the Searchbar Pro trigger (label + value lines inside the pro
// bar / mobile stage card, #853) instead of the legacy icon+input trigger; the
// dialog content and apply semantics are identical in both variants.
// `popover` (desktop pro bar instances only) anchors the open dialog under
// the bar as a scrimless popover — toolkit datepicker shape, full bar width.
// As of r7 (#936) the popover commits LIVE like the module (no Apply; a
// completed range closes it) — only the legacy centred modal still stages.
// `sheet` (mobile staged sheet, Eli r5) renders NO dialog: a stage header +
// the picker body expanded INLINE in the sheet's accordion (the module's
// --mobile-inline datepicker), committing LIVE on selection — the module's
// sheet calendar has no Apply and its footer is hidden there, so a staged
// draft would be silently dropped by the sheet's Search button.
export default function DateFilter({ pro = false, popover = false, sheet = false, sheetActive = false, onSheetActivate, advanceSignal = 0, onApplyAdvance }: { pro?: boolean; popover?: boolean; sheet?: boolean; sheetActive?: boolean; onSheetActivate?: () => void; advanceSignal?: number; onApplyAdvance?: () => void } = {}) {
  const { options, feedId } = useWidget<ExploreWidgetConfig>()
  const { state, setFilter, setFlexDates } = useExplorer()
  const cssString = useExplorerCss()
  const labels = useSbpLabels()
  const compactBar = useSbpCompact()
  const [open, setOpen] = useState(false)
  // Sheet stages have no open/close dialog cycle — the accordion's active
  // flag is their "open" for every open-keyed reseed/reset effect below.
  const effectiveOpen = sheet ? sheetActive : open
  const { triggerRef, measureNow, anchored, wrapperStyle } = useSbpPopover(popover, 'bar', open)

  // Chained popovers (#923): the bar coordinator bumps this to hand the hero
  // bar to this field (destination pick → WHEN).
  useEffect(() => {
    if (!advanceSignal) return
    measureNow()
    setOpen(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advanceSignal])

  // Transition memory for close-on-complete and the chain hand-off (#923, r7
  // #936) — the widget analog of the module's `hadCompleteRange`
  // (searchbar-pro.js:2427-2444), evaluated per day click now that the popover
  // commits live. Close/advance only when the draft went incomplete→complete
  // during THIS open: flexible-mode tweaks commit a full representative range
  // on EVERY chip tap, so acting on "complete" alone would yank the popover
  // away from a visitor adjusting an already-complete pick.
  const wentIncompleteRef = useRef(true)

  // --- Flexible dates (#870, slice 6) --------------------------------------
  // Pro-only, like every other Searchbar Pro behaviour in this file: the
  // reference's flex bridge lives inside the SBP picker and the legacy filter
  // bar has no equivalent surface.
  const flexCfg = options.searchbar.flexDates
  const flexEnabled = pro && flexCfg.enabled
  const flexStayOptions = flexCfg.stayLengths
  const todayStr = todayIso()
  // Bounded by the picker's own forward horizon. `monthsAhead` goes to 24
  // (config.ts), but every chip past `MAX_FORWARD_MONTHS - 1` can generate a
  // representative range the calendar cannot navigate to, so a visitor switching
  // back to Exact dates would find dates they cannot see or adjust. Probed over
  // every month option × stay length × alternative: a cap of 22 produces zero
  // escapes, 23 produces two. A no-op at the default 12; it costs two chips only
  // on a max-configured strip.
  //
  // Bounded HERE and not inside `representativeRange`/`enumerateAlts`: those are
  // straight transcriptions of the toolkit bridge, pinned by tests, and threading
  // a horizon through them would break parity with the reference.
  const flexMonthOptions = useMemo(
    () => monthOptions(Math.min(flexCfg.monthsAhead, MAX_FORWARD_MONTHS - 1), todayStr),
    [flexCfg.monthsAhead, todayStr],
  )
  // The APPLIED selection, only honoured while the feature is on and the admin
  // still offers that stay length — a link carrying a since-disabled chip reads
  // as an ordinary exact-date search rather than rehydrating a dead control.
  const appliedFlex =
    flexEnabled && state.flexDates && flexStayOptions.includes(state.flexDates.stay)
      ? state.flexDates
      : null

  // DRAFT flexible selection. Like the range and unlike the reference — which
  // commits and refines on every chip tap — these stage until Apply, because
  // that is this dialog's contract for every other control in it.
  const [flexMode, setFlexMode] = useState<'exact' | 'flexible'>('exact')
  const [flexStay, setFlexStay] = useState<FlexStayLength | null>(null)
  const [flexMonth, setFlexMonth] = useState<string | null>(null)
  // Admin-selectable display format for the trigger (#560, WP: explorer_date_format).
  // Custom formats are free text in the dashboard, so guard against tokens
  // date-fns rejects instead of letting the whole filter bar crash.
  const dateFormat = options.dateFormat ?? DEFAULT_DATE_FORMAT
  const formatDisplay = (date: Date) => {
    try {
      return format(date, dateFormat)
    } catch {
      return format(date, DEFAULT_DATE_FORMAT)
    }
  }

  // Eli QA 2026-08-05 item 3: the pro pill must read "Aug 12 – Sep 20", not
  // "Sep 15, 2026 – …". The toolkit has no format setting at all — it hardcodes
  // a locale-aware short formatter — but this widget has shipped `dateFormat`
  // since #560, so silently ignoring a configured value would be a regression.
  // Compromise: only the UNTOUCHED schema default is superseded. Any other
  // stored string is an explicit choice by that site and still wins, and the
  // legacy variant is left entirely alone.
  const dateFormatIsCustomised = !!options.dateFormat && options.dateFormat !== DEFAULT_DATE_FORMAT
  const formatTrigger = pro && !dateFormatIsCustomised ? formatShortDate : formatDisplay

  const [range, setRange] = useState<DateRange | undefined>(() => {
    const from = state.visitorFilters.checkin ? new Date(state.visitorFilters.checkin + 'T00:00:00') : undefined
    const to = state.visitorFilters.checkout ? new Date(state.visitorFilters.checkout + 'T00:00:00') : undefined
    return from || to ? { from, to } : undefined
  })

  // Re-seed the draft from the APPLIED filters on every open: the pro variant
  // mounts desktop and mobile instances side by side (#853), so an apply in
  // one would otherwise leave the other's mount-time draft stale.
  useEffect(() => {
    if (!effectiveOpen) return
    const from = state.visitorFilters.checkin ? new Date(state.visitorFilters.checkin + 'T00:00:00') : undefined
    const to = state.visitorFilters.checkout ? new Date(state.visitorFilters.checkout + 'T00:00:00') : undefined
    setRange(from || to ? { from, to } : undefined)
    // Seed the chain's transition memory from the APPLIED range: opening on
    // an already-complete range and applying it unchanged must not advance.
    wentIncompleteRef.current = !checkoutOf(from, to)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveOpen])

  // Any incomplete draft moment arms the hand-off — a fresh pick, a repick
  // (react-day-picker restarts the range on an inside click), or Clear Dates.
  useEffect(() => {
    if (effectiveOpen && !checkoutOf(range?.from, range?.to)) wentIncompleteRef.current = true
  }, [effectiveOpen, range])

  // Same re-seed for the flexible draft, and the rehydration path for a
  // `flex=flexible&stay_length=…&flex_month=…` deep link: the provider parsed
  // the URL into `state.flexDates` at mount, and opening the picker is what
  // puts the visitor back on the flexible panel with their chips lit.
  useEffect(() => {
    if (!effectiveOpen) return
    setFlexMode(appliedFlex ? 'flexible' : 'exact')
    setFlexStay(appliedFlex?.stay ?? null)
    setFlexMonth(appliedFlex?.month ?? null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveOpen])

  // Legacy read-only input. Keeps the configured `dateFormat` and its existing
  // "one endpoint prints alone" behaviour; only the separator is corrected to
  // the reference's en dash (it was a right arrow, U+2192). Check-out comes
  // from `checkoutOf` so a first click prints "Sep 15", not "Sep 15 – Sep 15".
  const displayValue = useMemo(() => {
    const checkout = checkoutOf(range?.from, range?.to)
    if (!range?.from && !checkout) return ''
    const fromStr = range?.from ? formatDisplay(range.from) : ''
    const toStr = checkout ? formatDisplay(checkout) : ''
    if (fromStr && toStr) return `${fromStr}${DATE_RANGE_SEPARATOR}${toStr}`
    return fromStr || toStr
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, dateFormat])

  // Pro trigger reads the APPLIED filters, not the local draft: with the pro
  // variant's dual desktop/mobile mounts, a sibling instance's apply would
  // otherwise leave this trigger's label stale until the dialog is opened.
  const vfDates = state.visitorFilters
  const appliedDisplay = useMemo(() => {
    const from = vfDates.checkin ? new Date(vfDates.checkin + 'T00:00:00') : undefined
    const to = vfDates.checkout ? new Date(vfDates.checkout + 'T00:00:00') : undefined
    // An applied flexible search names the stay length ahead of the dates it
    // resolved to — "1 week · Aug 15 – Aug 22" — so the pill says WHY those
    // dates are in play (toolkit `flexLabel`, searchbar-pro.js:1121-1135).
    if (appliedFlex) {
      return flexTriggerLabel(appliedFlex.stay, appliedFlex.month, vfDates.checkin, vfDates.checkout, formatTrigger)
    }
    // `checkoutOf`, not `to`: a legacy link carrying checkin === checkout is a
    // stay with no nights, and the reference calls that check-out pending. The
    // label function itself stays a straight transcription — the reference's
    // separate slots mean it can never be handed a same-day pair.
    return whenTriggerLabel(from, checkoutOf(from, to), formatTrigger, labels.addCheckout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vfDates.checkin, vfDates.checkout, dateFormat, pro, appliedFlex, labels.addCheckout])

  // EITHER endpoint counts as set — toolkit isWhenSet (js:3407-3410).
  const datesApplied = Boolean(vfDates.checkin || vfDates.checkout)

  // r6 item 2 (#935): while THIS instance's dialog is open, the pill mirrors
  // the DRAFT — the module field updates live as days are picked
  // (syncWhenTriggerLabel, searchbar-pro.js:713-727), including the
  // "Jul 15 – Add checkout" half-range. Still needed under r7's live commit
  // (#936): the popover only pushes COMPLETE ranges into the applied filters,
  // so this mirror is what phrases the half-range in between. Closed pills
  // keep reading the APPLIED filters, so a sibling instance's commit can
  // never leave this trigger stale (the reason it read applied-only before).
  // A flexible-mode draft mirrors only once its pick is complete — a lone
  // stay/month chip has no representative range to phrase yet.
  const draftDisplay = useMemo(() => {
    if (!open) return ''
    if (flexMode === 'flexible') {
      return flexStay && flexMonth
        ? flexTriggerLabel(
            flexStay, flexMonth,
            range?.from ? format(range.from, 'yyyy-MM-dd') : undefined,
            checkoutOf(range?.from, range?.to) ? format(checkoutOf(range?.from, range?.to) as Date, 'yyyy-MM-dd') : undefined,
            formatTrigger,
          )
        : ''
    }
    return whenTriggerLabel(range?.from, checkoutOf(range?.from, range?.to), formatTrigger, labels.addCheckout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, flexMode, flexStay, flexMonth, range, dateFormat, labels.addCheckout])

  // Live commit (module parity). Sheet (--mobile-inline): every selection
  // change commits as it happens; a lone check-in commits too — the module's
  // sheet state does the same and a lone checkin is inert upstream by design
  // (see handleApply). Desktop popover (r7 #936): the module has no Apply on
  // its date popover either — day clicks write state and sync the trigger
  // immediately (searchbar-pro.js:645-730) — but its refine layer skips
  // half-set ranges ("never on a single picked date", js:1522-1526), so the
  // popover path only pushes COMPLETE ranges (or a clear) into the applied
  // filters; the in-between shows through the trigger's draft mirror. The
  // flex triple rides along only when the committed range came from a
  // complete flexible pick. `checkoutOf` for the same 0-night reason as
  // handleApply below.
  const liveCommit = (
    r: DateRange | undefined,
    mode: 'exact' | 'flexible' = flexMode,
    stay: FlexStayLength | null = flexStay,
    mon: string | null = flexMonth,
  ) => {
    if (!sheet && !popover) return
    const checkout = checkoutOf(r?.from, r?.to)
    if (popover && r?.from && !checkout) return
    setFilter({
      checkin: r?.from ? format(r.from, 'yyyy-MM-dd') : undefined,
      checkout: checkout ? format(checkout, 'yyyy-MM-dd') : undefined,
    })
    if (flexEnabled) {
      setFlexDates(mode === 'flexible' && stay && mon ? { stay, month: mon } : null)
    }
  }

  const handleApply = () => {
    // `checkoutOf`, not `range.to`. One click leaves {from: d, to: d} behind
    // (see the helper), and applying that verbatim wrote checkin === checkout
    // into visitorFilters, the shared URL, and the Central availability query —
    // a 0-night search — while the pill claimed a complete range. Check-in
    // alone is a legitimate applied state: it is what "Sep 15 – Add checkout"
    // reports, and it is what the toolkit applies from the same gesture.
    // Upstream, a lone checkin is inert by design: Central applies its
    // availability filter only when checkin, checkout AND guests are all
    // present (PropertyController.php:230, verified live on beta 2026-08-05),
    // and the PDP quote fetches already gate on the complete pair.
    const checkout = checkoutOf(range?.from, range?.to)
    setFilter({
      checkin: range?.from ? format(range.from, 'yyyy-MM-dd') : undefined,
      checkout: checkout ? format(checkout, 'yyyy-MM-dd') : undefined,
    })
    // The flex triple is applied alongside the dates it produced, never on its
    // own: an incomplete flexible selection (a stay length with no month, say)
    // has no representative range, so it commits as an exact-date search and
    // the URL carries no flex markers.
    if (flexEnabled) {
      setFlexDates(flexMode === 'flexible' && flexStay && flexMonth ? { stay: flexStay, month: flexMonth } : null)
    }
    setOpen(false)
  }

  // Picker-footer "Clear Dates": DRAFT-ONLY. Every other action in this
  // dialog stages until Apply, and a clear that rewrote the URL mid-dialog
  // silently destroyed applied dates when the visitor then dismissed
  // (Eli decision 11, 2026-08-05 audit). Apply after clearing commits the
  // removal; dismissing keeps the applied dates.
  const handleClearDraft = () => {
    setRange(undefined)
    setHoverDate(undefined)
    // The stay length and month ARE the dates in flexible mode, so clearing the
    // dates has to release them too or the next Apply would re-commit the range
    // the visitor just cleared. The MODE is left alone — it is where the
    // visitor is standing, not something they cleared.
    setFlexStay(null)
    setFlexMonth(null)
  }
  // Reachable by name on purpose: the motion lane's closed-pill x button
  // calls this, and out there no dialog is open, so it commits immediately.
  const handleClear = () => {
    handleClearDraft()
    setFilter({ checkin: undefined, checkout: undefined })
    setFlexDates(null)
  }

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  // --- Date-window gates (#1338/#1339) --------------------------------------
  // `CalendarUtils` wants a property-style timezone to make "today" and the
  // advance-notice/window boundaries agree across a whole render, but the
  // explorer/SBP context has no single property (it searches many, before
  // any one is selected) and no feed-level timezone setting either — see the
  // data-source finding in the PR body. The visitor's own resolved IANA zone
  // is the closest available stand-in, and it is deliberately chosen to
  // match the `today` above (plain local-midnight `Date`, no zone math): both
  // now read "today" as the same calendar day, so the window/notice gates
  // below never disagree with the DayPicker's own `{ before: today }` rule.
  const dateWindowCfg = options.searchbar.dateWindow
  const timezone = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
    } catch {
      return 'UTC'
    }
  }, [])
  const calendarUtils = useMemo(() => new CalendarUtils({
    timezone,
    minStay: dateWindowCfg.minStay,
    // maxStay (a per-stay NIGHT ceiling) is a different toolkit concept from
    // maxBookingDays (how far ahead a visitor may search) — #1338/#1339 cover
    // minimum stay + advance notice + the booking-window ceiling only, and no
    // method called below (getMinstay/isNoticeBlocked/isOutsideWindow) ever
    // reads maxStay, so this is an inert constructor requirement, not a
    // second faked value.
    maxStay: 365,
    maxNumDays: dateWindowCfg.maxBookingDays,
    advanceNoticeHours: dateWindowCfg.advanceNoticeHours,
  }), [timezone, dateWindowCfg.minStay, dateWindowCfg.maxBookingDays, dateWindowCfg.advanceNoticeHours])

  // #1071 (central#3208): the fleet's real per-date minimum-stay aggregate —
  // `feed/explorer-meta`'s `minStayByDate`. Same feedId/platform/priceField
  // query key every other explorer-meta consumer in this widget already uses
  // (AdditionalFilters, ZoneFilter, LocationFilter, WhatPopover), so this
  // shares their cache entry instead of firing a second network request.
  // `effectiveMinStay` falls back to `dateWindowCfg.minStay` above (via
  // calendarUtils) whenever a date has no fleet entry — an unavailable feed,
  // no positive minimum anywhere, or an explorer-meta payload cached from
  // before this field existed.
  const priceField = priceFieldForMode(options.priceDisplay.noDates)
  const { data: metaData } = useFetchExplorerMeta(feedId, options.filter?.platform, priceField)
  const minStayByDate = metaData?.result?.minStayByDate

  // --- Date picker parity (#870, slice 2) ----------------------------------
  // The 6-months-stacked narrow layout is a Searchbar Pro behavior
  // (toolkit --mobile-inline); the legacy picker keeps its 2 months at every
  // width (codex fresh pass: viewport-only gating leaked SBP behavior into
  // the legacy variant).
  const isNarrowViewport = useNarrowViewport()
  const narrow = pro && isNarrowViewport
  // How many months the narrow column currently renders. The reference renders
  // exactly 6 and hides the arrows (searchbar-pro.js:823, css:3268-3270), which
  // leaves a touch visitor unable to reach ANY month past the fifth one out —
  // searching in December for the following August is impossible there. That is
  // a reference defect, and porting it would also regress this widget against
  // its own previous behaviour (develop rendered 2 months with working arrows at
  // every width, and no forward cap at all). DELIBERATE DIVERGENCE: the column
  // grows a page at a time as it is scrolled, which is what its own
  // overflow-y:auto container was already built for. Desktop still pages with
  // the arrows and is untouched.
  const [narrowMonths, setNarrowMonths] = useState(MONTHS_NARROW)
  const [hoverDate, setHoverDate] = useState<Date | undefined>(undefined)
  const [month, setMonth] = useState<Date>(today)
  const [slide, setSlide] = useState<'next' | 'prev' | null>(null)
  const slideTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Every state below keys off `checkoutOf` rather than off `to` being present
  // — see the helper for why react-day-picker's `{from: d, to: d}` means
  // "check-out pending" here.
  const draftCheckout = checkoutOf(range?.from, range?.to)
  const hasCheckout = !!draftCheckout
  const pickingCheckout = !!range?.from && !hasCheckout
  const hoverActive = pickingCheckout && !!hoverDate && atMidnight(hoverDate) > atMidnight(range!.from!)

  // #1338/#1339: precedence matches the toolkit's `blockReason` (c669a9b) —
  // booking window, then advance notice, then minimum stay. Min-stay only
  // applies while `pickingCheckout` — react-day-picker's range mode never
  // offers a second click on the check-in itself, so `range.from` is always
  // the candidate check-in here.
  const dateGated = (day: Date): boolean =>
    isBookingWindowBlocked(calendarUtils, day, today) ||
    isAdvanceNoticeBlocked(calendarUtils, day) ||
    (pickingCheckout && !!range?.from && isInMinStayWindow(calendarUtils, day, range.from, minStayByDate))

  const inSelectedRange = (day: Date) => {
    if (!hasCheckout) return false
    const t = atMidnight(day)
    return t > atMidnight(range!.from!) && t < atMidnight(range!.to!)
  }
  // Cells strictly between check-in and the cursor, matching the toolkit's
  // `applyHoverRange` (searchbar-pro.js:770-792).
  const inHoverRange = (day: Date) =>
    hoverActive && atMidnight(day) > atMidnight(range!.from!) && atMidnight(day) < atMidnight(hoverDate!)

  const dayModifiers = {
    // #1338/#1339: distinct from react-day-picker's own `disabled` class
    // (also applied — see the DayPicker `disabled` prop below — since a
    // gated day is functionally unselectable, not merely styled) so a
    // window/notice/min-stay day reads differently from a plain past day.
    // Guarded to today-or-later: past days are already `disabled` via
    // `{ before: today }` and must not also carry this class.
    blocked: (day: Date) => atMidnight(day) >= atMidnight(today) && dateGated(day),
    hoverRange: inHoverRange,
    // The cursor cell itself previews the check-out circle.
    checkoutPreview: (day: Date) => hoverActive && atMidnight(day) === atMidnight(hoverDate!),
    // Half-fill bridging an endpoint circle into the ribbon beside it. An
    // endpoint only earns one when a ribbon actually exists on that side —
    // a lone check-in, or a same-day range, stays a bare circle.
    rangeAfter: (day: Date) =>
      !!range?.from && atMidnight(day) === atMidnight(range.from) && (hoverActive || hasCheckout),
    rangeBefore: (day: Date) => hasCheckout && atMidnight(day) === atMidnight(range!.to!),
    // Month-boundary ribbon fades: the two panes are separated by a gap, so a
    // ribbon crossing the boundary fades out rather than butting up against
    // nothing (toolkit --range-fade-left / --range-fade-right). The reference
    // applies these to the HOVER preview as well as the committed range —
    // applyEdgeFades runs from applyHoverRange and its comment says
    // "selected (or hovered)" (codex r1: the first port faded only committed
    // ranges, so a preview crossing months ended abruptly).
    rangeFadeLeft: (day: Date) => (inSelectedRange(day) || inHoverRange(day)) && day.getDate() === 1,
    rangeFadeRight: (day: Date) =>
      (inSelectedRange(day) || inHoverRange(day)) &&
      day.getDate() === new Date(day.getFullYear(), day.getMonth() + 1, 0).getDate(),
  }

  const calBodyRef = useRef<HTMLDivElement>(null)

  // --- flexible-mode commit + height morph ---------------------------------

  /** Stage the representative range for a stay/month pair. Reference
   *  `commitFlexible` (searchbar-pro.js:1237-1254) minus the submit: the range
   *  it computes lands in the DRAFT, and Apply is what commits it (the sheet
   *  variant live-commits the returned range at its call sites instead). */
  const stageFlexRange = (stay: FlexStayLength | null, month: string | null): DateRange | null => {
    if (!stay || !month) return null
    const { checkin, checkout } = representativeRange(stay, month, todayStr)
    const staged = { from: new Date(checkin + 'T00:00:00'), to: new Date(checkout + 'T00:00:00') }
    setRange(staged)
    setHoverDate(undefined)
    return staged
  }

  // Height measured before the mode flip, consumed by the layout effect after
  // it. `height: auto` cannot be transitioned, so the reference measures both
  // ends and animates the delta (searchbar-pro.js:1274-1298); this is the same
  // trick split across React's render boundary.
  const morphFromRef = useRef<number | null>(null)
  const morphTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const switchFlexMode = (next: 'exact' | 'flexible') => {
    if (next === flexMode) return
    const el = calBodyRef.current
    morphFromRef.current = el && el.offsetHeight > 0 ? el.offsetHeight : null
    setFlexMode(next)
    // Leaving flexible keeps the dates already picked — the reference does the
    // same. The stay/month pair is DELIBERATELY retained rather than cleared:
    // `handleApply` already gates the flex triple on `flexMode === 'flexible'`,
    // so an exact submit reads as exact either way, and clearing here destroyed
    // the visitor's flexible selection on a within-one-session exact→flexible
    // round trip. Re-entering flexible then re-staged nothing (stageFlexRange
    // returns on a null pair), the chips went dark, and the next Apply silently
    // committed an EXACT search over the range the flexible one had left behind.
    // Nothing paints these values while the visitor stands in exact mode — the
    // chips and suggestions render only inside `showFlexPanel` — and an explicit
    // Clear Dates still releases them via `handleClearDraft`.
    if (next === 'flexible') {
      const staged = stageFlexRange(flexStay, flexMonth)
      if (staged) liveCommit(staged, 'flexible')
    } else if (checkoutOf(range?.from, range?.to)) {
      // Returning to Exact with a complete range commits it as exact dates,
      // dropping the flex triple — the module's mode switch re-syncs and
      // refines the same way, so nothing is ever silently converted (r7
      // #936). An incomplete draft commits nothing, same as day clicks.
      liveCommit(range, 'exact')
    }
  }

  useLayoutEffect(() => {
    const el = calBodyRef.current
    const start = morphFromRef.current
    morphFromRef.current = null
    if (!el || start === null) return
    const reduce = typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduce) return
    const end = el.offsetHeight
    if (start === end) return

    const release = () => {
      el.style.transition = ''
      el.style.height = ''
      el.style.overflow = ''
      el.style.boxSizing = ''
      el.removeEventListener('transitionend', onEnd)
      if (morphTimerRef.current) {
        clearTimeout(morphTimerRef.current)
        morphTimerRef.current = null
      }
    }
    function onEnd(e: TransitionEvent) {
      if (e.propertyName !== 'height') return
      release()
    }

    // box-sizing pinned inline so the px height means the same thing under any
    // host theme's box model (the reference pins it for that reason too).
    el.style.boxSizing = 'border-box'
    el.style.overflow = 'hidden'
    el.style.height = `${start}px`
    void el.offsetHeight // reflow, so the start height is what the browser animates FROM
    el.style.transition = `height ${FLEX_MORPH_MS}ms ease`
    el.style.height = `${end}px`
    el.addEventListener('transitionend', onEnd)
    if (morphTimerRef.current) clearTimeout(morphTimerRef.current)
    morphTimerRef.current = setTimeout(release, FLEX_MORPH_CLEAR_MS)
  }, [flexMode])

  useEffect(() => () => { if (morphTimerRef.current) clearTimeout(morphTimerRef.current) }, [])

  const handleMonthChange = (next: Date) => {
    // Slide travel is the grid's measured PIXEL width, written to a custom
    // property the keyframes read. The reference does the same measurement
    // (searchbar-pro.js:516-611, calendarsEl.offsetWidth) for a reason: the
    // weekday counter-transform only cancels when parent and child move by
    // the same absolute distance, which percentages of two different-width
    // boxes cannot do. The first port shipped an invented 6% for both.
    const months = calBodyRef.current?.querySelector<HTMLElement>('.hfse-rdp-months')
    if (months && months.offsetWidth > 0) {
      months.style.setProperty('--hfse-rdp-slide-px', `${months.offsetWidth}px`)
    }
    setSlide(next.getTime() > month.getTime() ? 'next' : 'prev')
    setMonth(next)
    if (slideTimer.current) clearTimeout(slideTimer.current)
    slideTimer.current = setTimeout(() => setSlide(null), SLIDE_CLEAR_MS)
  }
  useEffect(() => () => { if (slideTimer.current) clearTimeout(slideTimer.current) }, [])

  // On close: drop the hover preview, or a reopened picker would paint a
  // stale ribbon from the previous session's cursor. On open: seed the
  // visible month from the APPLIED check-in — the same visitorFilters
  // snapshot the range-reseed effect above uses. Reading the local `range`
  // here would see the stale mount-time draft: that effect's setRange has
  // not re-rendered yet when this one runs in the same open-flip commit
  // (codex fresh pass). Clamped so every visible pane stays inside
  // startMonth..endMonth.
  useEffect(() => {
    if (!effectiveOpen) {
      setHoverDate(undefined)
      return
    }
    // Each open starts the narrow column at one page again, seeded below.
    setNarrowMonths(MONTHS_NARROW)
    const first = new Date(today.getFullYear(), today.getMonth(), 1)
    const appliedFrom = state.visitorFilters.checkin
      ? new Date(state.visitorFilters.checkin + 'T00:00:00')
      : undefined
    if (!appliedFrom) {
      setMonth(first)
      return
    }
    const visible = narrow ? MONTHS_NARROW : MONTHS_DESKTOP
    const maxStart = new Date(today.getFullYear(), today.getMonth() + MAX_FORWARD_MONTHS - (visible - 1), 1)
    const start = new Date(appliedFrom.getFullYear(), appliedFrom.getMonth(), 1)
    setMonth(start < first ? first : start > maxStart ? maxStart : start)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveOpen])

  // A desktop↔narrow flip WHILE open changes the pane count, so the clamp
  // ceiling moves; re-clamp the current month (not re-seed — the visitor's
  // browse position survives the resize).
  useEffect(() => {
    if (!effectiveOpen) return
    const first = new Date(today.getFullYear(), today.getMonth(), 1)
    const visible = narrow ? MONTHS_NARROW : MONTHS_DESKTOP
    const maxStart = new Date(today.getFullYear(), today.getMonth() + MAX_FORWARD_MONTHS - (visible - 1), 1)
    setMonth((prev) => (prev < first ? first : prev > maxStart ? maxStart : prev))
    setNarrowMonths(MONTHS_NARROW)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [narrow])

  // Grow the narrow column as it is scrolled toward its end. The ceiling is the
  // horizon itself: react-day-picker stops emitting months past `endMonth` on
  // its own (helpers/getDisplayMonths.js breaks at calendarEndMonth), so an
  // over-count renders as "everything that is left", never past it.
  //
  // Wired through react-day-picker's own Months container rather than a native
  // addEventListener: `scroll` neither bubbles nor is delegated by React, so the
  // handler has to sit on the scrolling element — and that element is created
  // AFTER the open commit and then replaced, because DialogContent re-portals
  // into the elevated shadow host once useTopLevelPortal has one. A listener
  // attached by hand missed the node entirely. React re-attaches this one to
  // whichever node it ends up owning.
  const columnScroll = (e: UIEvent<HTMLDivElement>) => {
    if (!narrow) return
    const el = e.currentTarget
    if (el.scrollTop + el.clientHeight < el.scrollHeight - NARROW_GROW_PX) return
    setNarrowMonths((n) => Math.min(n + MONTHS_NARROW, MAX_FORWARD_MONTHS + 1))
  }
  const columnScrollRef = useRef(columnScroll)
  useEffect(() => { columnScrollRef.current = columnScroll })
  // Stable identity, deliberately: a component declared inline in `components`
  // is a new type on every render, and React would remount the entire grid —
  // mid-gesture — each time the hover preview moved.
  const MonthsContainer = useMemo(
    () => function MonthsContainer(p: HTMLAttributes<HTMLDivElement>) {
      return <div {...p} onScroll={(e) => columnScrollRef.current(e)} />
    },
    [],
  )

  // Toolkit searchbar-pro.js:725 — `clearBtn.disabled = !state.checkin &&
  // !state.checkout`. Keyed off the picker's DRAFT range (the reference's
  // state.checkin/checkout are likewise its own draft, not the applied filter),
  // so the control lights up the instant the first day is clicked.
  const clearDisabled = !range?.from && !range?.to

  // Flexible panel replaces the calendar entirely — the reference hides both
  // the grids and the month-nav pair behind [hidden] (searchbar-pro.js:
  // 1310-1313). Here the DayPicker simply does not render, which takes its nav
  // with it; the badge row's own min-height holds the header's height so
  // nothing above it shifts.
  const showFlexPanel = flexEnabled && flexMode === 'flexible'

  const draftAlts = useMemo(
    () => (flexEnabled && flexStay && flexMonth ? enumerateAlts(flexStay, flexMonth, todayStr) : []),
    [flexEnabled, flexStay, flexMonth, todayStr],
  )
  const draftCheckinIso = range?.from ? format(range.from, 'yyyy-MM-dd') : undefined
  const draftCheckoutIso = range?.to ? format(range.to, 'yyyy-MM-dd') : undefined

  // Badge row + flex toggle + flex panel/calendar — shared verbatim between
  // the dialog body and the sheet stage body (which wraps it in its own
  // hfse-date-calendar-body div so the morph/scroll machinery keeps working).
  const pickerBody = (
    <>
        {/* Nights badge — toolkit search-bar.php:526-531. aria-live so the
            count is announced as the visitor moves through the calendar.
            This row doubles as the picker header: the DayPicker's month-nav
            pair is lifted into its right side by explorer.css (decision 26 —
            the reference shares one space-between row between badge and
            arrows), so the row must stay the picker's previous sibling. */}
        <div className="hfse-rdp-badge-row">
          <span className="hfse-rdp-nights-badge" aria-live="polite" aria-atomic="true">
            {nightsBadgeLabel(
              range?.from,
              draftCheckout,
              range?.from ? effectiveMinStay(calendarUtils, range.from, minStayByDate) : 1,
            )}
          </span>
        </div>
        {/* Exact / Flexible mode toggle, toolkit search-bar.php:539-544 — it
            sits between the header row and the calendars in the reference too. */}
        {flexEnabled && (
          <div className="hfse-sbp-flex-toggle" role="group" aria-label="Date mode">
            {(['exact', 'flexible'] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                className={`hfse-sbp-flex-mode${flexMode === mode ? ' hfse-sbp-flex-mode-active' : ''}`}
                aria-pressed={flexMode === mode}
                onClick={() => switchFlexMode(mode)}
              >
                {mode === 'exact' ? 'Exact dates' : "I'm flexible"}
              </button>
            ))}
          </div>
        )}

        {showFlexPanel && (
          <div className="hfse-sbp-flex-panel">
            <p className="hfse-sbp-flex-heading">How long would you like to stay?</p>
            <div className="hfse-sbp-flex-stays">
              {flexStayOptions.map((stay) => (
                <button
                  key={stay}
                  type="button"
                  className={`hfse-sbp-flex-chip${flexStay === stay ? ' hfse-sbp-flex-chip-active' : ''}`}
                  aria-pressed={flexStay === stay}
                  onClick={() => {
                    setFlexStay(stay)
                    const staged = stageFlexRange(stay, flexMonth)
                    if (staged) liveCommit(staged, 'flexible', stay, flexMonth)
                  }}
                >
                  {FLEX_STAY_LABELS[stay]}
                </button>
              ))}
            </div>

            <p className="hfse-sbp-flex-heading">When do you want to go?</p>
            <div className="hfse-sbp-flex-months">
              {flexMonthOptions.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  className={`hfse-sbp-flex-chip${flexMonth === opt.key ? ' hfse-sbp-flex-chip-active' : ''}`}
                  aria-pressed={flexMonth === opt.key}
                  onClick={() => {
                    setFlexMonth(opt.key)
                    const staged = stageFlexRange(flexStay, opt.key)
                    if (staged) liveCommit(staged, 'flexible', flexStay, opt.key)
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {/* In-panel suggested dates: narrow to a specific week or month
                without leaving the picker (toolkit search-bar.php:569-572).
                Styled as secondary to the two chip groups above it. */}
            {!!draftAlts.length && flexStay && (
              <div className="hfse-sbp-flex-suggested">
                <p className="hfse-sbp-flex-heading">{FLEX_ALT_HEADINGS[flexStay]}</p>
                <div className="hfse-sbp-flex-suggested-track">
                  {draftAlts.map((alt) => {
                    const active = alt.checkin === draftCheckinIso && alt.checkout === draftCheckoutIso
                    return (
                      <button
                        key={`${alt.checkin}-${alt.checkout}`}
                        type="button"
                        className={`hfse-sbp-flex-chip${active ? ' hfse-sbp-flex-chip-active' : ''}`}
                        aria-current={active || undefined}
                        onClick={() => {
                          const picked = {
                            from: new Date(alt.checkin + 'T00:00:00'),
                            to: new Date(alt.checkout + 'T00:00:00'),
                          }
                          setRange(picked)
                          setHoverDate(undefined)
                          liveCommit(picked, 'flexible')
                        }}
                      >
                        {alt.label}
                      </button>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {!showFlexPanel && (
        <DayPicker
          mode="range"
          selected={range}
          onSelect={(r) => {
            setRange(r)
            // A committed click supersedes the mouse preview; without this a
            // keyboard/mouse mix could keep painting a stale hover ribbon.
            setHoverDate(undefined)
            // Live-apply: day taps happen in exact mode only (the DayPicker
            // does not render behind the flex panel).
            liveCommit(r, 'exact')
            // Module day-click semantics (r7 #936, searchbar-pro.js:2428-2445):
            // a range that goes incomplete→complete under a DAY CLICK closes
            // the popover — on the inline explorer the grid already refreshed
            // live, and in redirect/hero mode the (heroMode-gated) advance
            // opens the guest picker instead. The incomplete→complete guard
            // keeps flex-chip taps (which commit a complete representative
            // range on EVERY tap, outside this handler) and reopened-complete
            // drafts from yanking the popover shut.
            if (popover && checkoutOf(r?.from, r?.to) && wentIncompleteRef.current) {
              setOpen(false)
              onApplyAdvance?.()
            }
          }}
          month={month}
          onMonthChange={handleMonthChange}
          onDayMouseEnter={(day) => setHoverDate(day)}
          onDayMouseLeave={() => setHoverDate(undefined)}
          numberOfMonths={narrow ? narrowMonths : MONTHS_DESKTOP}
          // #1338/#1339: `dateGated` layers the min-stay/advance-notice/
          // booking-window rules on top of the existing past-date rule —
          // an array of matchers, not a replacement (see react-day-picker's
          // `Matcher` type). A gated day never completes a click here, same
          // as a past day; the `blocked` modifier above only adds the CSS
          // distinction.
          disabled={[{ before: today }, dateGated]}
          startMonth={today}
          endMonth={forwardHorizon(today)}
          fixedWeeks
          // Single-letter weekday headers (#924): the module ships
          // ['S','M','T','W','T','F','S'] (searchbar-pro.js:92); Intl narrow
          // is the locale-aware spelling of the same default.
          formatters={{
            formatWeekdayName: (day: Date) => {
              try {
                return new Intl.DateTimeFormat(undefined, { weekday: 'narrow' }).format(day)
              } catch {
                return 'SMTWTFS'[day.getDay()]
              }
            },
          }}
          modifiers={dayModifiers}
          modifiersClassNames={{
            blocked: 'hfse-rdp-blocked',
            hoverRange: 'hfse-rdp-hover-range',
            checkoutPreview: 'hfse-rdp-checkout-preview',
            rangeAfter: 'hfse-rdp-range-after',
            rangeBefore: 'hfse-rdp-range-before',
            rangeFadeLeft: 'hfse-rdp-range-fade-left',
            rangeFadeRight: 'hfse-rdp-range-fade-right',
          }}
          classNames={{
            root: `hfse-rdp${narrow ? ' hfse-rdp-narrow' : ''}`,
            months: `hfse-rdp-months${slide ? ` hfse-rdp-slide-${slide}` : ''}`,
            month: 'hfse-rdp-month',
            // Table + tbody get their own classes so explorer.css can stretch
            // the grid to the flexed month pane and give week rows the
            // module's 2px column gap (#930) — the default rdp-month_grid
            // shrink-wraps to content and centres badly in the 850px panel.
            month_grid: 'hfse-rdp-month-grid',
            weeks: 'hfse-rdp-weeks',
            month_caption: 'hfse-rdp-caption',
            nav: 'hfse-rdp-nav',
            button_previous: 'hfse-rdp-nav-btn',
            button_next: 'hfse-rdp-nav-btn',
            weekdays: 'hfse-rdp-weekdays',
            weekday: 'hfse-rdp-weekday',
            week: 'hfse-rdp-week',
            day: 'hfse-rdp-day',
            day_button: 'hfse-rdp-day-btn',
            selected: 'hfse-rdp-selected',
            range_start: 'hfse-rdp-range-start',
            range_end: 'hfse-rdp-range-end',
            range_middle: 'hfse-rdp-range-middle',
            today: 'hfse-rdp-today',
            outside: 'hfse-rdp-outside',
            disabled: 'hfse-rdp-disabled',
          }}
          components={{
            Months: MonthsContainer,
            Chevron: ({ orientation }) => (
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {orientation === 'left'
                  ? <polyline points="15 18 9 12 15 6" />
                  : <polyline points="9 18 15 12 9 6" />
                }
              </svg>
            ),
          }}
        />
        )}
    </>
  )

  const renderContent = () => (
    <DialogContent
      className={`hfse-form-modal hfse-form-date-options${pro ? ' hfse-sbp-popover' : ''}${anchored ? ' hfse-sbp-popover-anchored' : ''}`}
      overlayClassName={pro ? `hfse-sbp-popover-overlay${anchored ? ' hfse-sbp-popover-overlay-anchored' : ''}` : undefined}
      wrapperStyle={wrapperStyle}
      elevateDom
      cssString={cssString}
      title="When"
      hideClose={anchored}
      // Non-modal popovers (#923) dismiss on outside POINTER + Escape only,
      // never on focus moving — the module's popovers behave the same. Without
      // this, a chained hand-off self-destructs: the dialog that just closed
      // returns focus to ITS trigger, which reads as focus-outside to the
      // popover the chain just opened (real-browser find; happy-dom's weak
      // focus semantics never reproduced it).
      onFocusOutside={popover ? (e) => e.preventDefault() : undefined}
    >
      {/* Anchored popovers are headerless like the toolkit's (close =
          outside-click/Escape); the centred modal and mobile sheet keep the
          visible header + X. */}
      {!anchored && (
        <div className="hk-modal-header">
          <div className="hk-h4">When</div>
        </div>
      )}

      <div className="hk-modal-body hfse-date-calendar-body" ref={calBodyRef}>{pickerBody}</div>

      <div className="hk-modal-footer">
        {/* Picker-footer clear, toolkit .hrtk-sbp-datepicker__clear
            (search-bar.php:577-583, css:2153-2179). The reference ships it
            DISABLED and re-enables it as soon as EITHER endpoint exists
            (searchbar-pro.js:725); it never closes the popover.
            The :disabled treatment — muted colour, not-allowed cursor, and the
            underline REMOVED rather than merely recoloured — is applied inline
            because explorer.css belongs to the concurrent motion lane on this
            wave. It should move into a `.hfse-modal-reset-button:disabled`
            rule when the lanes land. */}
        <button
          type="button"
          className="hk-button hk-button-link hfse-modal-reset-button hfse-show-always"
          // Popover commits live (r7 #936), so its clear commits too — the
          // module's Clear Dates writes state and refines in the same breath
          // (no staged draft exists to protect). The legacy modal keeps the
          // draft-only clear: there, Apply is still the commit point and a
          // mid-dialog URL rewrite destroyed applied dates (Eli decision 11,
          // 2026-08-05 audit).
          onClick={popover ? handleClear : handleClearDraft}
          disabled={clearDisabled}
          style={clearDisabled
            ? { color: 'var(--hfse-muted-foreground, #9ca3af)', cursor: 'not-allowed', textDecoration: 'none' }
            : undefined}
        >
          Clear dates
        </button>
        {/* No Apply on the popover (r7 #936): the module's date popover has no
            footer action at all — selections commit per click and completing
            the range closes it (searchbar-pro.js:2428-2445). The legacy modal
            keeps its staged contract. */}
        {!popover && (
          <button
            type="button"
            className="hk-button hk-button-small bg-accent text-accent-foreground hk-margin-auto-left hfse-modal-next-button"
            onClick={handleApply}
            style={{ marginLeft: 'auto' }}
          >
            Apply
          </button>
        )}
      </div>
    </DialogContent>
  )

  if (sheet) {
    return (
      <>
        <button
          type="button"
          className={`hfse-sbp-field hfse-sbp-field-btn hfse-sbp-date-field hfse-sbp-stage-header${datesApplied ? ' hfse-sbp-field-has-value' : ''}`}
          aria-expanded={sheetActive}
          onClick={onSheetActivate}
        >
          <span className="hfse-sbp-field-label">{labels.whenLabel}</span>
          <span className={`hfse-sbp-field-value ${appliedDisplay ? '' : 'hfse-sbp-field-placeholder'}`}>
            {appliedDisplay || labels.whenIdle}
          </span>
          {datesApplied && <FieldClear label="Clear dates" onClear={handleClear} />}
        </button>
        {sheetActive && (
          // hfse-date-calendar-body keeps the height-morph + narrow-scroll
          // machinery working on the inline mount (calBodyRef reads it).
          <div className="hfse-sbp-stage-body hfse-date-calendar-body" ref={calBodyRef}>
            <h3 className="hfse-sbp-stage-question">When&#39;s your trip?</h3>
            {pickerBody}
          </div>
        )}
      </>
    )
  }

  if (pro) {
    return (
      // Measuring inside the open flip (not an effect) batches the anchor
      // rect with the open state, so the portal's first paint is already
      // positioned — no centred flash.
      //
      // Desktop popovers are NON-modal (#923 / SBP-5): Radix modal sets
      // `pointer-events: none` on the body, so a click on another bar field
      // while this one was open only DISMISSED — the visitor needed a second
      // click (measured: two-click switching). Non-modal keeps the page
      // interactive, exactly the module's scrimless popovers, and the same
      // pointerdown that closes this field lets its click open the next —
      // the module's one-click setActiveField(next). Outside-close and
      // Escape still work (DismissableLayer flags inside-ness via a capture
      // handler on the content, so the elevated shadow root is safe); the
      // scroll/resize re-measure in useSbpPopover keeps the anchor attached
      // now that the scroll lock is gone. The mobile sheet stays modal.
      <Dialog modal={!popover} open={open} onOpenChange={(o) => { if (o) measureNow(); setOpen(o) }}>
        <DialogTrigger asChild>
          <button
            type="button"
            ref={triggerRef}
            className={`hfse-sbp-field hfse-sbp-field-btn hfse-sbp-date-field${datesApplied ? ' hfse-sbp-field-has-value' : ''}`}
          >
            {compactBar.compact ? <SrLabel>{labels.whenLabel}</SrLabel> : <span className="hfse-sbp-field-label">{labels.whenLabel}</span>}
            {/* Value truncation is CSS only (.hfse-sbp-field-value carries
                white-space:nowrap + text-overflow:ellipsis), matching the
                reference — never a JS substring. While the dialog is open the
                value mirrors the DRAFT (r6 item 2); closed, the applied
                filters (see draftDisplay). */}
            <span className={`hfse-sbp-field-value ${(open ? draftDisplay : appliedDisplay) ? '' : 'hfse-sbp-field-placeholder'}`}>
              {(open ? draftDisplay : appliedDisplay) || (compactBar.compact ? compactBar.placeholderWhen : labels.whenIdle)}
            </span>
            {/* handleClear commits (it calls setFilter), so the pill clear
                writes APPLIED state rather than the dialog's draft — the
                toolkit's clearField does the same (js:3457-3533). */}
            {datesApplied && <FieldClear label="Clear dates" onClear={handleClear} />}
          </button>
        </DialogTrigger>
        {renderContent()}
      </Dialog>
    )
  }

  return (
    <div className="hfse-form-date-field-container">
      <div className="field-control hfse-form-date-field-control">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <div className="hfse-form-field-trigger">
              <span className="hfse-form-field-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
              </span>
              <input
                type="text"
                className="hk-input hfse-form-date-input"
                placeholder="Anytime"
                value={displayValue}
                readOnly
              />
            </div>
          </DialogTrigger>
          {renderContent()}
        </Dialog>
      </div>
    </div>
  )
}
